import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Bell, BellSlash, MapPin, Phone as PhoneIcon, ArrowRight, Money, SignOut, ChatCircleText, Star } from '@phosphor-icons/react';
import API from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { useBranding } from '../../lib/branding';
import { useUnread } from '../../lib/unread';
import { playDoorbell } from '../../lib/sound';

const hoursSince = (iso) => {
  if (!iso) return 0;
  return (Date.now() - new Date(iso).getTime()) / 3.6e6;
};
const ageColor = (h) => h > 12 ? '#ef4444' : h > 5 ? '#f59e0b' : '#10b981';

const OrderCard = ({ o, onAccept, onReject }) => {
  const nav = useNavigate();
  const h = hoursSince(o.created_at);
  const color = ageColor(h);
  const [, setTick] = useState(0);
  useEffect(() => { const iv = setInterval(() => setTick(t => t + 1), 60000); return () => clearInterval(iv); }, []);
  return (
    <div className="pk-card p-4">
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="font-mono text-[10px] tracking-widest uppercase text-neutral-500">#{o.order_id.slice(-10)}</div>
          <div className="mt-1 font-bold text-[14px]">{o.items.map(i => `${i.quantity}× ${i.product_name}`).join(', ')}</div>
          <div className="text-[11px] text-neutral-500 mt-1">{o.items[0]?.material} · {o.items[0]?.size || (o.items[0]?.width ? `${o.items[0].width}x${o.items[0].height} ft` : '')}</div>
        </div>
        <div className="text-right">
          <div className="font-stat text-[22px] leading-none">₹{(o.total_amount || 0).toLocaleString('en-IN')}</div>
          <span className="inline-flex items-center gap-1 mt-1 px-2 py-0.5 text-[10px] font-bold text-white" style={{ background: color }}>
            {h < 1 ? `${Math.round(h * 60)}m` : `${h.toFixed(1)}h`} old
          </span>
        </div>
      </div>
      <div className="mt-3 text-[11px] flex items-center gap-3">
        <span className="font-mono"><MapPin size={12} className="inline mr-1" />PIN {o.delivery_pincode}</span>
        <span className="font-mono opacity-70"><PhoneIcon size={12} className="inline mr-1" />{o.delivery_phone_masked ? '•••••• hidden' : (o.delivery_phone || '—')}</span>
      </div>
      <div className="mt-3 grid grid-cols-3 gap-2">
        <button onClick={() => onAccept(o)} className="pk-btn pk-btn-magenta col-span-1" data-testid={`accept-${o.order_id}`}>Accept</button>
        <button onClick={() => onReject(o)} className="pk-btn pk-btn-outline col-span-1" data-testid={`reject-${o.order_id}`}>Reject</button>
        <button onClick={() => nav(`/orders/${o.order_id}`)} className="pk-btn pk-btn-outline col-span-1" data-testid={`view-${o.order_id}`}>View</button>
      </div>
    </div>
  );
};

const PartnerDashboard = () => {
  const { user, logout, refresh } = useAuth();
  const { branding } = useBranding();
  const { counts: unreadCounts } = useUnread();
  const nav = useNavigate();

  const [orders, setOrders] = useState([]);
  const [earnings, setEarnings] = useState(null);
  const [reviews, setReviews] = useState(null);
  const [isOnline, setIsOnline] = useState(user?.is_online !== false);
  const [muted, setMuted] = useState(() => localStorage.getItem('pk_muted') === '1');
  const isOnlineRef = useRef(isOnline);
  const mutedRef = useRef(muted);
  useEffect(() => { isOnlineRef.current = isOnline; }, [isOnline]);
  useEffect(() => { mutedRef.current = muted; }, [muted]);

  const load = async () => {
    try {
      const r = await API.get('/orders');
      setOrders(r.data || []);
      const e = await API.get('/partners/me/earnings');
      setEarnings(e.data);
      const rv = await API.get('/partners/me/reviews');
      setReviews(rv.data);
    } catch { /* ignore */ }
  };

  useEffect(() => { load(); const iv = setInterval(load, 10000); return () => clearInterval(iv); }, []);

  // Looping doorbell: play once per pending order every ~7s while any pending exists and unmuted
  useEffect(() => {
    const iv = setInterval(() => {
      if (mutedRef.current) return;
      if (!isOnlineRef.current) return;
      const pending = orders.filter(o => o.status === 'pending_partner' && !o.partner_id);
      if (pending.length === 0) return;
      for (let i = 0; i < Math.min(pending.length, 4); i++) {
        setTimeout(() => playDoorbell(0.3), i * 500);
      }
    }, 7000);
    return () => clearInterval(iv);
  }, [orders]);

  // First-time nudge on mount
  useEffect(() => {
    if (muted || !isOnline) return;
    const pending = orders.filter(o => o.status === 'pending_partner' && !o.partner_id);
    if (pending.length > 0) {
      for (let i = 0; i < Math.min(pending.length, 3); i++) setTimeout(() => playDoorbell(0.35), i * 700);
    }
    // eslint-disable-next-line
  }, [orders.length > 0]);

  const doAction = async (path, oid) => {
    try {
      await API.post(`/orders/${oid}/${path}`);
      toast.success(path === 'accept' ? 'Accepted' : path === 'reject' ? 'Rejected' : 'Done');
      load();
    } catch (e) {
      const detail = e.response?.data?.detail || 'Failed';
      toast.error(detail);
      // If someone else grabbed it — refresh
      if (e.response?.status === 409) load();
    }
  };

  const toggleMute = async () => {
    // Muting = going offline. When muted, partner is offline and doesn't receive jobs.
    const nowMuted = !muted;
    setMuted(nowMuted);
    localStorage.setItem('pk_muted', nowMuted ? '1' : '0');
    try {
      await API.post('/partners/me/online', { is_online: !nowMuted });
      setIsOnline(!nowMuted);
      refresh();
      toast(nowMuted ? 'You are now OFFLINE — sound muted, jobs paused' : 'Back ONLINE — receiving jobs');
      load();
    } catch { toast.error('Could not update status'); }
  };

  const grouped = useMemo(() => ({
    pending: orders.filter(o => o.status === 'pending_partner' && !o.partner_id),
    active: orders.filter(o => ['accepted', 'printing', 'printed', 'out_for_delivery'].includes(o.status)),
    delivered: orders.filter(o => o.status === 'delivered'),
  }), [orders]);

  return (
    <div className="min-h-screen bg-neutral-100">
      <style>{`@keyframes pk-blink { 0%,100% { opacity: 1; } 50% { opacity: 0.35; } } .pk-blink { animation: pk-blink 1.1s ease-in-out infinite; }`}</style>

      <header className="bg-white border-b-2 border-black sticky top-0 z-40">
        <div className="max-w-[1400px] mx-auto px-6 h-16 flex items-center justify-between">
          <div>
            <div className="font-display text-[20px] leading-none">{branding.logo_text}</div>
            <div className="text-[9px] tracking-[0.22em] uppercase text-neutral-500 mt-1">Partner Console · {user?.shop_name || user?.name}</div>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-1.5 text-[10px] tracking-[0.22em] uppercase font-bold">
              <span className={`w-2 h-2 rounded-full ${isOnline ? '' : 'pk-blink'}`} style={{ background: isOnline ? '#10b981' : '#ef4444' }} />
              {isOnline ? 'Online' : 'Offline'}
            </div>
            <button
              onClick={toggleMute}
              className={`pk-chip ${muted ? 'pk-blink' : ''}`}
              style={muted ? { background: '#ef4444', color: '#fff', borderColor: '#0a0a0a' } : {}}
              title="Toggle sound & online"
              data-testid="mute-toggle"
            >
              {muted ? <BellSlash size={14} weight="fill" /> : <Bell size={14} weight="fill" />}
              {muted ? 'Muted · Offline' : 'Sound on'}
            </button>
            <Link to="/partner/earnings" className="pk-btn pk-btn-outline" data-testid="open-earnings-btn"><Money size={14} /> Earnings</Link>
            <button onClick={async () => { await logout(); nav('/'); }} className="pk-chip"><SignOut size={12} /> Logout</button>
          </div>
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto p-6 space-y-6">
        {user?.partner_status !== 'approved' && (
          <div className="pk-card p-4" style={{ background: 'var(--pk-yellow)' }}>
            <div className="font-bold text-[14px]">Your account is <span className="uppercase">{user?.partner_status || 'pending review'}</span></div>
            <div className="text-[12px] mt-1">Our team is reviewing your application. Once approved you'll start receiving jobs here.</div>
          </div>
        )}

        {muted && (
          <div className="pk-card p-4 border-4" style={{ borderColor: '#ef4444', background: '#fef2f2' }}>
            <div className="font-bold text-[14px]" style={{ color: '#ef4444' }}>You are OFFLINE</div>
            <div className="text-[12px] mt-1">Turn sound back on to become online and receive job cards again.</div>
          </div>
        )}

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="pk-card p-4">
            <div className="text-[9px] tracking-[0.22em] uppercase text-neutral-500">Pending jobs</div>
            <div className="font-stat text-[36px] leading-none mt-1">{grouped.pending.length}</div>
          </div>
          <div className="pk-card p-4">
            <div className="text-[9px] tracking-[0.22em] uppercase text-neutral-500">Active</div>
            <div className="font-stat text-[36px] leading-none mt-1">{grouped.active.length}</div>
          </div>
          <div className="pk-card p-4">
            <div className="text-[9px] tracking-[0.22em] uppercase text-neutral-500">Delivered ({earnings?.window_label || ''})</div>
            <div className="font-stat text-[36px] leading-none mt-1">{earnings?.month_count ?? 0}</div>
            <div className="text-[9px] text-neutral-400 mt-1">Stats reset monthly</div>
          </div>
          <div className="pk-card p-4" style={{ background: 'var(--pk-yellow)' }}>
            <div className="text-[9px] tracking-[0.22em] uppercase">Earnings ({earnings?.window_label || ''})</div>
            <div className="font-stat text-[30px] leading-none mt-1">₹{(earnings?.month_earnings || 0).toLocaleString('en-IN')}</div>
          </div>
        </div>

        <section>
          <div className="flex items-center gap-3 mb-3">
            <div className="text-[10px] tracking-[0.24em] uppercase font-bold" style={{ color: 'var(--pk-primary)' }}>— Live Feed · Pending</div>
            <span className="pulse-dot w-2 h-2 rounded-full" style={{ background: 'var(--pk-primary)' }} />
            <span className="text-[10px] text-neutral-500 tracking-wider">First to accept gets the job.</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {grouped.pending.map(o => (
              <OrderCard key={o.order_id} o={o} onAccept={(x) => doAction('accept', x.order_id)} onReject={(x) => doAction('reject', x.order_id)} />
            ))}
            {grouped.pending.length === 0 && <div className="col-span-full text-[13px] text-neutral-500 py-6 text-center">No pending jobs. You'll hear a doorbell when one arrives.</div>}
          </div>
        </section>

        <section>
          <div className="text-[10px] tracking-[0.24em] uppercase font-bold mb-3">— Active</div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {grouped.active.map(o => {
              const count = unreadCounts[o.order_id] || 0;
              const hasUnread = count > 0;
              return (
                <Link
                  key={o.order_id}
                  to={`/orders/${o.order_id}`}
                  className={`pk-card p-4 relative block ${hasUnread ? 'brutal-shadow-magenta' : ''}`}
                  style={hasUnread ? { borderColor: 'var(--pk-primary)' } : {}}
                  data-testid={`partner-active-${o.order_id}`}
                >
                  {hasUnread && (
                    <>
                      <span
                        className="absolute -top-[2px] -left-[2px] flex items-center gap-1 px-2 py-0.5 border-2 border-black text-white text-[9px] tracking-[0.18em] uppercase font-bold"
                        style={{ background: 'var(--pk-primary)' }}
                        data-testid={`partner-unread-flag-${o.order_id}`}
                      >
                        <ChatCircleText size={11} weight="fill" />
                        New msg
                      </span>
                      <span
                        className="absolute top-2 right-2 min-w-[22px] h-5 px-1.5 border-2 border-black text-white text-[10px] font-bold flex items-center justify-center font-mono"
                        style={{ background: 'var(--pk-primary)' }}
                        data-testid={`partner-unread-count-${o.order_id}`}
                      >
                        {count}
                      </span>
                    </>
                  )}
                  <div className={`font-mono text-[10px] uppercase tracking-widest ${hasUnread ? 'text-black font-bold mt-3' : 'text-neutral-500'}`}>#{o.order_id.slice(-10)}</div>
                  <div className={`mt-1 leading-tight ${hasUnread ? 'font-extrabold text-[15px]' : 'font-bold text-[14px]'}`}>
                    {o.items.map(i => `${i.quantity}× ${i.product_name}`).join(', ')}
                  </div>
                  <div className="mt-2 flex items-center justify-between">
                    <span className={`status-${o.status} border-2 border-black text-[9px] tracking-widest uppercase px-1.5 py-0.5 font-bold`}>{o.status}</span>
                    <ArrowRight size={14} />
                  </div>
                </Link>
              );
            })}
            {grouped.active.length === 0 && <div className="col-span-full text-[13px] text-neutral-500 py-6 text-center">No active jobs.</div>}
          </div>
        </section>

        <section>
          <div className="text-[10px] tracking-[0.24em] uppercase font-bold mb-3">— Your rating</div>
          <div className="pk-card p-5" data-testid="partner-rating-panel">
            {reviews?.count ? (
              <div className="grid md:grid-cols-3 gap-6">
                <div className="text-center md:border-r-2 md:border-black md:pr-6">
                  <div className="font-stat text-[64px] leading-none" style={{ color: '#e30066' }}>{reviews.avg?.toFixed(1)}</div>
                  <div className="flex items-center justify-center gap-0.5 mt-2">
                    {[1, 2, 3, 4, 5].map(n => (
                      <Star key={n} size={20} weight="fill" color={n <= Math.round(reviews.avg) ? '#f59e0b' : '#e5e5e5'} />
                    ))}
                  </div>
                  <div className="text-[10px] tracking-widest uppercase text-neutral-500 mt-2">{reviews.count} review{reviews.count === 1 ? '' : 's'}</div>
                </div>
                <div className="md:col-span-2 space-y-1">
                  {[5, 4, 3, 2, 1].map(n => {
                    const c = reviews.breakdown?.[n] || 0;
                    const pct = reviews.count ? (c / reviews.count) * 100 : 0;
                    return (
                      <div key={n} className="flex items-center gap-2 text-[11px]">
                        <div className="w-3 font-mono font-bold">{n}</div>
                        <Star size={10} weight="fill" color="#f59e0b" />
                        <div className="flex-1 h-2 bg-neutral-100 border border-black overflow-hidden">
                          <div className="h-full" style={{ width: `${pct}%`, background: '#f59e0b' }} />
                        </div>
                        <div className="w-6 text-right font-mono text-neutral-500">{c}</div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : (
              <div className="text-[12px] text-neutral-500 text-center py-4">No ratings yet — deliver orders to start earning stars.</div>
            )}
            {reviews?.reviews?.length > 0 && (
              <div className="mt-5 pt-4 border-t-2 border-black space-y-3">
                <div className="text-[9px] tracking-[0.22em] uppercase font-bold text-neutral-500">Latest reviews</div>
                {reviews.reviews.slice(0, 5).map(r => (
                  <div key={r.review_id} className="pk-card p-3" data-testid={`review-${r.review_id}`}>
                    <div className="flex items-center justify-between">
                      <div className="font-bold text-[13px]">{r.customer_name}</div>
                      <div className="flex gap-0.5">
                        {[1, 2, 3, 4, 5].map(n => (
                          <Star key={n} size={12} weight="fill" color={n <= r.stars ? '#f59e0b' : '#e5e5e5'} />
                        ))}
                      </div>
                    </div>
                    {r.text && <div className="text-[12px] text-neutral-700 mt-1 italic">&ldquo;{r.text}&rdquo;</div>}
                    <div className="text-[10px] font-mono text-neutral-400 mt-1">{r.created_at?.slice(0, 10)}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>
      </main>
    </div>
  );
};

export default PartnerDashboard;
